// Service 17 — ล่ามราชการ / ล่ามศาล / ล่ามชั้นสอบสวน สำหรับชาวต่างชาติในไทย
// อ้างอิง: ประมวลกฎหมายวิธีพิจารณาความอาญา มาตรา 13 และ 13/1 (การจัดหาล่ามในชั้นสอบสวนและชั้นศาล),
// ประมวลกฎหมายวิธีพิจารณาความแพ่ง มาตรา 46 (ภาษาที่ใช้ในศาลและการแปลเอกสาร),
// สำนักงานศาลยุติธรรม (coj.go.th) — บัญชีล่ามและอัตราค่าตอบแทนล่ามของศาล,
// กรมการปกครอง/สำนักบริหารการทะเบียน (bora.dopa.go.th) — การจดทะเบียนครอบครัวที่มีคู่สมรสต่างชาติ,
// สำนักงานตรวจคนเข้าเมือง (immigration.go.th), กรมที่ดิน (dol.go.th),
// สภาทนายความในพระบรมราชูปถัมภ์ (lawyerscouncil.or.th) — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร,
// กรมการกงสุล (consular.mfa.go.th) — นิติกรณ์และรับรองคำแปล
// Category definitions only — client-safe, no heavy FAQ payload.

export type InterpreterFaqCategory =
  | "court-interpreter"
  | "police-investigation"
  | "amphur-family-registry"
  | "immigration-office"
  | "land-office"
  | "notarial-attorney"
  | "embassy-consular"
  | "hospital-medical"
  | "business-contract"
  | "qualification-ethics";

export interface InterpreterCategoryDef {
  slug: InterpreterFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const INTERPRETER_FAQ_CATEGORIES: InterpreterCategoryDef[] = [
  {
    slug: "court-interpreter",
    titleTh: "ล่ามในศาล (คดีอาญาและคดีแพ่ง)",
    titleEn: "Court interpreters (criminal and civil cases)",
    descTh:
      "สิทธิที่จะมีล่ามในการพิจารณาคดี การจัดหาล่ามโดยศาลตาม ป.วิ.อ. มาตรา 13 บัญชีล่ามของสำนักงานศาลยุติธรรม การแปลเอกสารประกอบคำฟ้องตาม ป.วิ.พ. มาตรา 46 และการสาบานตนของล่าม",
    descEn:
      "The right to an interpreter at trial, court-appointed interpreters under section 13 of the Criminal Procedure Code, the Office of the Judiciary interpreter roster, translation of exhibits under section 46 of the Civil Procedure Code, and the interpreter's oath.",
    matchTh: /ศาล|พิจารณาคดี|คำฟ้อง|สาบาน|ป\.วิ\.อ|ป\.วิ\.พ|พยาน|คำเบิกความ|ล่ามศาล/i,
    matchEn: /court|trial|hearing|witness|testimony|oath|judiciary|criminal procedure|civil procedure/i,
  },
  {
    slug: "police-investigation",
    titleTh: "ล่ามชั้นสอบสวนและสถานีตำรวจ",
    titleEn: "Interpreters at police stations and during investigation",
    descTh:
      "การแจ้งความ การให้ปากคำ การสอบปากคำผู้ต้องหาและผู้เสียหายที่ไม่เข้าใจภาษาไทย หน้าที่ของพนักงานสอบสวนในการจัดหาล่าม การลงลายมือชื่อในบันทึกคำให้การ และประเด็นที่ทำให้บันทึกถูกโต้แย้งภายหลัง",
    descEn:
      "Filing a police report, giving a statement, questioning of suspects and complainants who do not understand Thai, the investigating officer's duty to arrange an interpreter, signing the record of statement, and what can make a statement contestable later.",
    matchTh: /ตำรวจ|สอบสวน|แจ้งความ|ปากคำ|ผู้ต้องหา|ผู้เสียหาย|บันทึกคำให้การ|สถานี/i,
    matchEn: /police|investigat|statement|suspect|complainant|report|custody|station/i,
  },
  {
    slug: "amphur-family-registry",
    titleTh: "ล่ามที่อำเภอ / งานทะเบียนครอบครัว",
    titleEn: "Interpreters at the district office and family registry",
    descTh:
      "การจดทะเบียนสมรส หย่า รับรองบุตร และบันทึกข้อตกลงที่สำนักทะเบียนอำเภอ เมื่อคู่สมรสฝ่ายหนึ่งไม่เข้าใจภาษาไทย รวมถึงพยานและการแปลเอกสารประกอบคำร้อง",
    descEn:
      "Marriage, divorce, legitimation and settlement agreements recorded at the district (amphur) registry when one party does not understand Thai, including witnesses and translation of supporting documents.",
    matchTh: /อำเภอ|สำนักทะเบียน|จดทะเบียนสมรส|หย่า|รับรองบุตร|ทะเบียนครอบครัว|บันทึกข้อตกลง/i,
    matchEn: /district office|amphur|registrar|marriage|divorce|legitimation|family registry/i,
  },
  {
    slug: "immigration-office",
    titleTh: "ล่ามที่สำนักงานตรวจคนเข้าเมือง",
    titleEn: "Interpreters at immigration offices",
    descTh:
      "การสัมภาษณ์ขออยู่ต่อ การตรวจสอบที่พักอาศัย การสัมภาษณ์วีซ่าติดตามครอบครัว การชี้แจงกรณี overstay และการกรอกแบบคำขอที่มีเฉพาะภาษาไทย",
    descEn:
      "Extension-of-stay interviews, residence verification visits, dependent-visa interviews, explaining overstay cases, and completing application forms that exist only in Thai.",
    matchTh: /ตรวจคนเข้าเมือง|ตม\.|วีซ่า|อยู่ต่อ|overstay|สัมภาษณ์|ที่พักอาศัย|TM\s?30/i,
    matchEn: /immigration|visa|extension of stay|overstay|interview|residence|TM\s?30/i,
  },
  {
    slug: "land-office",
    titleTh: "ล่ามที่สำนักงานที่ดินและการโอนกรรมสิทธิ์",
    titleEn: "Interpreters at the land office and property transfers",
    descTh:
      "การโอนกรรมสิทธิ์ห้องชุด การจดสัญญาเช่าระยะยาว การจดสิทธิเก็บกิน และการลงนามในสัญญาต่อหน้าเจ้าพนักงานที่ดินเมื่อผู้ซื้อหรือผู้ขายเป็นชาวต่างชาติ",
    descEn:
      "Condominium transfers, registration of long leases and usufructs, and signing before the land officer when the buyer or seller is a foreigner.",
    matchTh: /ที่ดิน|โอนกรรมสิทธิ์|ห้องชุด|สัญญาเช่า|สิทธิเก็บกิน|เจ้าพนักงานที่ดิน|จำนอง/i,
    matchEn: /land office|transfer|condominium|lease|usufruct|mortgage|title deed/i,
  },
  {
    slug: "notarial-attorney",
    titleTh: "ล่ามกับทนายความผู้ทำคำรับรองลายมือชื่อ (Notarial Services Attorney)",
    titleEn: "Interpreters and Notarial Services Attorneys",
    descTh:
      "เมื่อผู้ลงนามไม่เข้าใจภาษาของเอกสาร ทนายความผู้ทำคำรับรองลายมือชื่อต้องมั่นใจว่าผู้ลงนามเข้าใจเนื้อหา บทบาทของล่ามในการรับรองลายมือชื่อ คำสาบาน และหนังสือมอบอำนาจข้ามประเทศ",
    descEn:
      "Where the signer does not understand the language of the document, the Notarial Services Attorney must be satisfied the signer understands it — the interpreter's role in signature attestation, affidavits and cross-border powers of attorney.",
    matchTh: /ทนายความผู้ทำคำรับรอง|notarial|รับรองลายมือชื่อ|affidavit|มอบอำนาจ|สภาทนายความ/i,
    matchEn: /notarial|notary|attestation|affidavit|power of attorney|lawyers council/i,
  },
  {
    slug: "embassy-consular",
    titleTh: "ล่ามที่สถานทูต สถานกงสุล และกรมการกงสุล",
    titleEn: "Interpreters at embassies, consulates and the Department of Consular Affairs",
    descTh:
      "การนัดหมายทำหนังสือรับรองสถานะ การสาบานตนที่สถานทูต การยื่นนิติกรณ์ที่กรมการกงสุล และความต่างระหว่างล่ามพูดกับการรับรองคำแปลเอกสาร",
    descEn:
      "Appointments for affirmations and single-status letters, swearing before embassy officers, submitting documents for legalisation at the Department of Consular Affairs, and the difference between spoken interpreting and certified translation.",
    matchTh: /สถานทูต|สถานกงสุล|กรมการกงสุล|นิติกรณ์|รับรองคำแปล|apostille|สาบานตน/i,
    matchEn: /embassy|consulate|consular affairs|legalisation|legalization|certified translation|apostille|affirmation/i,
  },
  {
    slug: "hospital-medical",
    titleTh: "ล่ามการแพทย์ในโรงพยาบาล",
    titleEn: "Medical interpreting in hospitals",
    descTh:
      "การให้ความยินยอมก่อนการรักษา การอธิบายผลตรวจและใบรับรองแพทย์ การเคลมประกัน และการรักษาความลับข้อมูลสุขภาพตาม PDPA",
    descEn:
      "Informed consent before treatment, explaining results and medical certificates, insurance claims, and confidentiality of health data under Thailand's PDPA.",
    matchTh: /โรงพยาบาล|แพทย์|รักษา|ยินยอม|ประกัน|PDPA|ผู้ป่วย|ล่ามการแพทย์/i,
    matchEn: /hospital|medical|doctor|consent|patient|insurance|PDPA|clinical/i,
  },
  {
    slug: "business-contract",
    titleTh: "ล่ามธุรกิจ การประชุม และการลงนามสัญญา",
    titleEn: "Business, meeting and contract-signing interpreters",
    descTh:
      "ล่ามในการประชุมผู้ถือหุ้นและกรรมการ การเจรจาสัญญา การตรวจสอบวิเคราะห์สถานะ (due diligence) การเปิดบัญชีธนาคาร และการเลือกระหว่างล่ามพูดตามและล่ามพูดพร้อม",
    descEn:
      "Interpreting at shareholder and board meetings, contract negotiations, due diligence, bank account opening, and choosing between consecutive and simultaneous interpreting.",
    matchTh: /ประชุม|สัญญา|เจรจา|ธุรกิจ|ธนาคาร|due diligence|ผู้ถือหุ้น|พูดพร้อม|พูดตาม/i,
    matchEn: /meeting|contract|negotiat|business|bank|due diligence|shareholder|simultaneous|consecutive/i,
  },
  {
    slug: "qualification-ethics",
    titleTh: "คุณสมบัติ จรรยาบรรณ และการจัดจ้างล่าม",
    titleEn: "Interpreter qualifications, ethics and engagement",
    descTh:
      "คุณสมบัติของล่าม การขึ้นบัญชีล่ามของศาล ความเป็นกลางและการขัดกันแห่งผลประโยชน์ การรักษาความลับ การเตรียมเอกสารล่วงหน้า และวิธีติดต่อจัดหาล่ามกับทีมงาน",
    descEn:
      "Interpreter qualifications, the court interpreter roster, impartiality and conflicts of interest, confidentiality, advance preparation, and how to arrange an interpreter with our team.",
    matchTh: /คุณสมบัติ|จรรยาบรรณ|เป็นกลาง|ความลับ|ขึ้นบัญชี|จัดจ้าง|จัดหาล่าม|เตรียม|ติดต่อ/i,
    matchEn: /qualif|ethic|impartial|confidential|roster|engage|prepare|book|arrange|contact/i,
  },
];

export function getInterpreterCategoryDef(slug: string): InterpreterCategoryDef | undefined {
  return INTERPRETER_FAQ_CATEGORIES.find((c) => c.slug === slug);
}